import { useEffect, useState, useMemo } from 'react';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useAppSelector } from 'hooks/useRedux';
import { RootState } from 'store';
import { http } from 'middleware/apiMiddleware';
import { businessPlans } from 'constants/apiUrls';
import { FETCH_STATES } from 'enum/common';
import { Loader } from 'components/Common/Loader';
import Render from 'components/Common/Render';
import PromotionPlans from 'components/Home/PromotionPlans';
import { checkIsAnywhereCommunity } from 'utils/common';
import { toast } from 'react-hot-toast';
import useMixpanel from 'hooks/useMixpanel';
import InstantPayTutorials from 'components/Tutorials/InstantPayTutorial.tsx/InstantPayTutorial';
import { InstantPayModalDataType } from 'containers/Home';
import BusinessHeader from 'components/Home/BusinessHeader';

const InstantPay = () => {
  const [selectedBusiness] = useLocalStorage('selectedBusiness', null);
  const businessState = useAppSelector((state: RootState) => state.businesses);
  const business: BusinessType = businessState?.business || selectedBusiness || null;
  const { trackEvent } = useMixpanel();

  const [plans, setPlans] = useState<{
    status: FETCH_STATES;
    data: BusinessPlanType[];
  }>({ status: FETCH_STATES.LOADING, data: [] });

  const [showInstantpayTutorial, setShowInstantpayTutorial] =
    useState<InstantPayModalDataType>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [updatingAmount, setUpdatingAmount] = useState(false);

  const isAnyWhereCommunity = checkIsAnywhereCommunity(business);

  const fetchPromotionPlans = async (businessUuid: string) => {
    try {
      setPlans({ status: FETCH_STATES.LOADING, data: [] });
      const { data, error } = await http.get(businessPlans(businessUuid));
      if (error) {
        setPlans({ status: FETCH_STATES.FAILED, data: [] });
      }
      setPlans({ status: FETCH_STATES.SUCECSS, data });
    } catch {
      setPlans({ status: FETCH_STATES.FAILED, data: [] });
    }
  };

  const activateInstantPay = (moduleId: string) => {
    setShowInstantpayTutorial({
      moduleId,
    });
  };

  const deActivateOffer = async (businessPlan: BusinessPlanType) => {
    try {
      trackEvent('deactivate_offer_button_Clicking', {
        planId: businessPlan?.uuid,
      });

      const { data, error } = await http.patch(
        `/business/${business?.uuid}/business-paid-modules/${businessPlan?.uuid}`,
        {
          enabled: false,
        },
      );
      if (!!data && !error) {
        toast.success('Plan deactivated successfully');
      }
      trackEvent('deactivate_offer_success', {
        planId: businessPlan?.uuid ?? '',
      });
      fetchPromotionPlans(business?.uuid);
    } catch (error) {
      trackEvent('deactivate_offer_failure', {
        planId: businessPlan?.uuid ?? '',
      });
      toast.error('Unexpected error occurred while deactivating plan');
    }
  };

  const updateTopupAmount = async (moduleId: string, topUpAmount: number) => {
    try {
      setUpdatingAmount(true);
      const { data, error } = await http.patch(
        `/business/${business?.uuid}/business-paid-modules/${moduleId}`,
        {
          enabled: true,
          formData: { autoTopUpAmount: topUpAmount },
        },
      );
      if (!!data && !error) {
        toast.success('Topup amount updated successfully');
        trackEvent('update_topup_amount_success', {
          planId: moduleId,
          topUpAmount,
        });
      }
    } catch (error) {
      trackEvent('update_topup_amount_failure', {
        planId: moduleId,
        topUpAmount,
        error,
      });
      toast.error('Unexpected error occurred while updating topup amount');
    } finally {
      setUpdatingAmount(false);
    }
  };

  const onInstantPayActivationSuccess = () => {
    setShowInstantpayTutorial(null);
    fetchPromotionPlans(business?.uuid);
  };

  useEffect(() => {
    if (business) {
      fetchPromotionPlans(business?.uuid);
    }
  }, [business?.uuid]);

  const instantPayPlans = useMemo(
    () => plans?.data?.filter((plan) => plan?.module?.label === 'Instant Pay') || [],
    [plans?.data],
  );

  const isLoading = plans?.status === FETCH_STATES.LOADING;

  return (
    <div className="bg-white p-8 pt-6 h-full flex-col flex gap-5">
      <Render when={!!business}>
        <BusinessHeader selectedBusiness={business} />
        <Render when={isLoading}>
          <div className="flex justify-center items-center h-screen">
            <Loader size={8} color={'darkBlue'} />
          </div>
        </Render>
        <Render when={!isLoading}>
          <PromotionPlans
            activateOffer={() => {}}
            deActivateOffer={deActivateOffer}
            plans={instantPayPlans}
            updateTopupAmount={updateTopupAmount}
            isUpdatingAmount={updatingAmount}
            isAnyWhereCommunity={isAnyWhereCommunity}
            business={business}
            errorMessage={errorMessage}
            setErrorMessage={(value) => setErrorMessage(value)}
            activateInstantPay={activateInstantPay}
            activateSubscription={() => {}}
          />
        </Render>
        <Render when={!!showInstantpayTutorial}>
          <InstantPayTutorials
            onSuccess={onInstantPayActivationSuccess}
            showInstantpayTutorial={showInstantpayTutorial}
            setShowInstantpayTutorial={setShowInstantpayTutorial}
          />
        </Render>
      </Render>
    </div>
  );
};

export default InstantPay;
